<template>
  <admin-wrapper
    title-translation-path="admin.loginSettingsTitle"
    @change="onSettingChanged"
    :inputs="inputs"
    @save="save"
  >
    <template #user_name_max_length.input.inner="{ value }">
      <v-row no-gutters>
        <v-col cols="auto">
          <span class="text-body-2">
            {{ value }}
          </span>
        </v-col>
      </v-row>
    </template>
    <template #user_name_min_length.input.inner="{ value }">
      <v-row no-gutters>
        <v-col cols="auto">
          <span class="text-body-2">
            {{ value }}
          </span>
        </v-col>
      </v-row>
    </template>
    <template #passwordRequirments.input>
      <v-col>
        <password-requirement-input
          v-for="input in passwordRequirementInputs"
          :key="input.name"
          v-bind="input"
          ref="input"
        />
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import AdminWrapper from '@/components/AdminWrapper';
import PasswordRequirementInput from '@/components/PasswordRequirementInput';

import { get } from 'vuex-pathify';

export default {
  name: 'LoginSettings',
  components: {
    AdminWrapper,
    PasswordRequirementInput,
  },
  data: () => ({
    is2FAEnabled: false,
  }),
  watch: {
    twoFAEnabled: {
      immediate: true,
      handler(nV) {
        this.is2FAEnabled = nV;
      },
    },
  },
  computed: {
    twoFAEnabled: get('admin/settingsValues@two_fa_module_enabled'),
    twoFASuperadminEnabled: get('admin/additionalModules@TWO_FA.enabled'),
    isCurrentUserSuperadmin: get('users/currentUser@is_superadmin'),
    passwordRequirementInputs() {
      return [
        {
          translationPath: 'admin.lowerCase',
          name: 'password_at_least_one_lower_case',
          'hide-details': true,
          dataType: {
            name: 'checkbox',
          },
        },
        {
          translationPath: 'admin.upperCase',
          name: 'password_at_least_one_upper_case',
          'hide-details': true,
          dataType: {
            name: 'checkbox',
          },
        },
        {
          translationPath: 'admin.digit',
          name: 'password_at_least_one_digit',
          'hide-details': true,
          dataType: {
            name: 'checkbox',
          },
        },
        {
          translationPath: 'admin.specialChar',
          name: 'password_at_least_one_special_char',
          'hide-details': true,
          dataType: {
            name: 'checkbox',
          },
          additionalText: "!@#$%^&*(){}[]|:”;'<>,./",
          // regex [!@#\$%\^&\*(){}\[\]\\|:”;\\'<>,.]+
        },
      ];
    },
    inputs() {
      return [
        {
          name: 'user_name_max_length',
          translationPath: 'admin.usernameMaxLength',
          dataType: {
            name: 'integer',
          },
        },
        {
          name: 'user_name_min_length',
          translationPath: 'admin.usernameMinLength',
          dataType: {
            name: 'integer',
          },
        },
        {
          name: 'max_failed_logins',
          translationPath: 'admin.loginAttempts',
          subtitle: 'admin.noRestrictions',
          align: 'start',
          allowNegative: false,
          dataType: {
            name: 'integer',
          },
        },
        {
          name: 'password_expire_days',
          translationPath: 'admin.passwordLife',
          subtitle: 'admin.noRestrictions',
          allowNegative: false,
          align: 'start',
          dataType: {
            name: 'integer',
          },
        },
        {
          name: 'password_recovery_validity_hours',
          translationPath: 'admin.reminderLink',
          subtitle: 'admin.noRestrictions',
          allowNegative: false,
          align: 'start',
          dataType: {
            name: 'integer',
          },
        },
        {
          name: 'password_minimum_length',
          translationPath: 'admin.passwordMinLength',
          align: 'baseline',
          allowNegative: false,
          dataType: {
            name: 'integer',
          },
        },
        {
          name: 'passwordRequirments',
          translationPath: 'admin.passwordRequirments',
          allowNegative: false,
          align: 'start',
        },
        ...(this.isCurrentUserSuperadmin
          ? [
              {
                name: 'single_login_per_user',
                translationPath: 'admin.singleLogin',
                subtitle: 'admin.singleLoginInfo',
                dataType: {
                  name: 'switch',
                },
              },
            ]
          : []),
        ...(this.twoFASuperadminEnabled
          ? [
              {
                name: 'two_fa_module_enabled',
                translationPath: 'admin.2FA',
                dataType: {
                  name: 'switch',
                },
              },
              ...(this.is2FAEnabled
                ? [
                    {
                      name: 'two_fa_verification_method',
                      translationPath: 'admin.2FAVerificationMethod',
                      items: [
                        {
                          value: 'email',
                          text: this.$i18n.t('admin.2FAVerificationMethods.email'),
                        },
                      ],
                      rules: this.is2FAEnabled ? [v => !!v || 'rules.required'] : null,
                      dataType: {
                        name: 'select',
                      },
                    },
                  ]
                : []),
            ]
          : []),
      ];
    },
  },
  methods: {
    onSettingChanged(ev, name) {
      if (name !== 'two_fa_module_enabled') return;
      this.is2FAEnabled = ev;
    },
    save() {
      this.$refs.input.forEach(input => {
        input.save();
      });
    },
  },
};
</script>

<style lang="scss"></style>
